import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { TopConfig } from '../context/ConfigContext';
import { CarbucksReservation, CarbucksTimeslot, CarbucksCustomer } from '../types/services';
import { getLocalizedNameOfDay } from '../helpers/dates';
import * as logger from '../utils/logger';

export type GetTimeslotsParams = {
    /**
     * The start date for the timeslot search (ISO 8601 format, e.g. '2025-08-15')
     */
    startDate: string;
    /**
     * The end date for the timeslot search (ISO 8601 format, e.g. '2025-08-15')
     */
    endDate: string;
    /**
     * The unique ID identifying the selected Carbucks site, e.g. '888e4567-e89b-12d3-a456-426614174978'
     */
    carbucksGUID: string;
};

export type CreateReservationParams = {
    /**
     * The licenseplate for the damaged car
     */
    licencePlate: string; // e.g. "AB12345"
    /**
     * The unique ID identifying the selected Carbucks site, e.g. '888e4567-e89b-12d3-a456-426614174978'
     */
    carbucksGUID: string;
    /**
     * The start date and time for the selected timeslot, e.g. "2025-07-01 10:30:00"
     */
    slotStartDateTime: string;
    /**
     * The end date and time for the selected timeslot, e.g. "2025-07-01 12:30:00"
     */
    slotEndDateTime: string;
};

export type UpdateReservationParams = {
    /**
     * The reservation ID of the existing reservation to update.
     */
    reservationGUID: string;
    /**
     * The order ID of the existing reservation to update.
     */
    orderGUID: string;
    /**
     * The unique ID identifying the selected Carbucks site, e.g. '888e4567-e89b-12d3-a456-426614174978'
     */
    carbucksGUID: string;
    /**
     * The start date and time for the selected timeslot, e.g. "2025-07-01 10:30:00"
     */
    slotStartDateTime: string;
    /**
     * The end date and time for the selected timeslot, e.g. "2025-07-01 12:30:00"
     */
    slotEndDateTime: string;
};

export type SendCustomerInfoParams = {
    fullName: string;
    msisdn: string;
    dialCode: string;
    number: string;
    address: string;
    addressStreet: string;
    addressStreetNumber: string;
    addressZipcode: string;
    addressCity: string;
    addressCountry: string;
    email: string;
    orderGUID: string;
};

export abstract class CarbucksService {
    static async getTimeslots(topConfig: TopConfig, params: GetTimeslotsParams): Promise<CarbucksTimeslot[] | null> {
        const { url, headers, method } = topConfig.services.carbucks.timeslots;

        if (!url) {
            throw new Error('Carbucks service: timeslots URL is not defined');
        }

        try {
            const response: AxiosResponse<CarbucksTimeslot[]> = await axios(url, {
                method,
                headers,
                params
            });

            return response.status === 200 ? response.data : null;
        } catch (error) {
            logger.logError('Carbucks Timeslots API failed!');
            logger.logError(`Error details: ${JSON.stringify(error)}`);
            return null;
        }
    }

    static async createReservation(
        topConfig: TopConfig,
        params: CreateReservationParams
    ): Promise<CarbucksReservation | null> {
        const { create } = topConfig.services.carbucks.reservation;

        if (!create.url) {
            throw new Error('Carbucks service: reservation.create URL is not defined');
        }

        const data = {
            carPlate: params.licencePlate,
            carbucksGUID: params.carbucksGUID,
            slotStartDatetime: params.slotStartDateTime,
            slotEndDatetime: params.slotEndDateTime
        };

        try {
            const requestConfig: AxiosRequestConfig = {
                ...create,
                url: create.url,
                data: data,
                headers: {
                    ...create.headers
                }
            };

            const response: AxiosResponse<CarbucksReservation> = await axios(requestConfig);

            return [200, 201].includes(response.status) ? response.data : null;
        } catch (error) {
            logger.logError('Carbucks Reservation API failed! (createReservation)');
            logger.logError(`Error details: ${JSON.stringify(error)}`);
            return null;
        }
    }

    static async updateReservation(
        topConfig: TopConfig,
        params: UpdateReservationParams
    ): Promise<CarbucksReservation | null | boolean> {
        const { update } = topConfig.services.carbucks.reservation;

        if (!update.url) {
            throw new Error('Carbucks service: reservation.update URL is not defined');
        }

        const data = {
            reservationGUID: params.reservationGUID,
            orderGUID: params.orderGUID,
            carbucksGUID: params.carbucksGUID,
            slotStartDatetime: params.slotStartDateTime,
            slotEndDatetime: params.slotEndDateTime
        };

        try {
            const requestConfig: AxiosRequestConfig = {
                ...update,
                url: update.url,
                data: data,
                headers: {
                    ...update.headers
                }
            };

            const response: AxiosResponse<CarbucksReservation> = await axios(requestConfig);

            if (response.status === 201) {
                return response.data;
            }

            if (response.status === 204) {
                return true;
            }

            return null;
        } catch (error) {
            logger.logError('Carbucks Reservation API failed! (updateReservation)');
            logger.logError(`Error details: ${JSON.stringify(error)}`);
            return null;
        }
    }

    static async validateReservation(topConfig: TopConfig, params: UpdateReservationParams): Promise<boolean | null> {
        const { update } = topConfig.services.carbucks.reservation;

        if (!update.url) {
            throw new Error('Carbucks service: reservation.update URL is not defined');
        }

        const data = {
            reservationGUID: params.reservationGUID,
            orderGUID: params.orderGUID,
            carbucksGUID: params.carbucksGUID,
            slotStartDatetime: params.slotStartDateTime,
            slotEndDatetime: params.slotEndDateTime
        };

        try {
            const requestConfig: AxiosRequestConfig = {
                ...update,
                url: update.url,
                data: data,
                headers: {
                    ...update.headers
                }
            };

            const response: AxiosResponse<CarbucksReservation> = await axios(requestConfig);

            // We consider the reservation valid if we get one of these status codes:
            // 201: reservation updated
            // 204: reservation found, but no changed needed (this should be the normal scenario on a validateReservation request, but we accept 201 as well to be more resilient)
            return [201, 204].includes(response.status);
        } catch (error) {
            logger.logError('Carbucks Reservation API failed! (validateReservation)');
            logger.logError(`Error details: ${JSON.stringify(error)}`);
            return null;
        }
    }

    static async sendCustomerInfo(
        topConfig: TopConfig,
        params: SendCustomerInfoParams
    ): Promise<CarbucksCustomer | null> {
        const { url, headers, method } = topConfig.services.carbucks.customer;

        if (!url) {
            throw new Error('Carbucks service: customer URL is not defined');
        }
        const customerUrl = url + '/' + params.orderGUID + '/customer';

        const data = {
            fullName: params.fullName,
            msisdn: params.msisdn,
            dialCode: params.dialCode,
            number: params.number,
            address: params.address,
            addressStreet: params.addressStreet,
            addressStreetNumber: params.addressStreetNumber,
            addressZipcode: params.addressZipcode,
            addressCity: params.addressCity,
            addressCountry: params.addressCountry,
            email: params.email
        };

        try {
            const response: AxiosResponse = await axios(customerUrl, { method, headers, data: data });
            logger.logInfo(`Carbucks Customer API response for orderGUID ${params.orderGUID} : `, response.data);
            return response.status === 200 ? response.data : null;
        } catch (error) {
            logger.logError('Carbucks Customer API failed! (updateCustomer)');
            logger.logError(`Error details: ${JSON.stringify(error)}`);
            return null;
        }
    }

    /**
     * Extracts the reservation date from a given Carbucks reservation date-time string.
     *
     * @param reservationDateTime - The reservation date and time as a string, e.g. "2025-07-01 18:30:00.000000"
     * @returns The extracted reservation date as a `Date` object.
     */
    static extractReservationDate = (reservationDateTime: string): Date => {
        const datePart = reservationDateTime.split(' ')[0]; // "2025-07-01"
        return new Date(datePart);
    };

    /**
     * Extracts the reservation time from a given Carbucks reservation date-time string
     *
     * @param reservationDateTime - The reservation date and time as a string, e.g. "2025-07-01 18:30:00.000000"
     * @returns The time portion of the reservation as a string (e.g., "14:30").
     */
    static extractReservationTime = (reservationDateTime: string): string => {
        if (!reservationDateTime || !reservationDateTime.includes(' ')) {
            return '';
        }

        const timePart = reservationDateTime.split(' ')[1]; // "18:30:00.000000"
        const [hours, minutes] = timePart.split(':');
        return `${hours}:${minutes}`;
    };

    /**
     * Formats a reservation date and time string into a human-readable, localized format.
     *
     * The formatted string includes the localized name of the day, the day and month,
     * and the time, in the format: "{DayName} d. {Day}/{Month} kl {Time}", e.g. "Tirsdag d. 1/7 kl 16:35"
     *
     * @param reservationDateTime - The reservation date and time as a string.
     * @returns The formatted reservation date and time string.
     */
    static prettyFormatReservationDateTime = (reservationDateTime: string): string => {
        const date = CarbucksService.extractReservationDate(reservationDateTime);
        const time = CarbucksService.extractReservationTime(reservationDateTime);

        return `${getLocalizedNameOfDay(date)} d. ${date.getDate()}/${date.getMonth() + 1} kl. ${time}`;
    };
}
